import React, { FC, Fragment, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { configureForClient } from 'config/Amplify';
import { AuthenticationType } from 'api/public';
import { AuthState } from 'types/aws-amplify';
import { Typography } from '@material-ui/core';

import { useAuth, useClient, useNotify } from 'hooks';
import { federatedSignIn, filterErrorDescription, isSignedOut } from 'helpers';
import storage from 'storage';
import { IAuthContainerProps } from '../types';
import SignIn from './SignIn';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import RequireNewPassword from './RequireNewPassword';
import ConfirmSignUp from './ConfirmSignUp';
import Invitation from './Invitation';
import ClientSelect from './ClientSelect';
import RouteController from './RouteController';
import Suspended from './Suspended';

const Layout: FC<RouteComponentProps> = () => {
  const [password, setPassword] = useState('');
  const client = useClient();
  const { data, update } = useAuth();
  const notify = useNotify();
  const ssoConfiguration = client?.sso_configuration;
  const authState = data.state;
  const clientIsSSO = client?.authentication_type === AuthenticationType.Sso && ssoConfiguration;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorDescription = urlParams.get('error_description');
    if (errorDescription) {
      notify.error({
        component: (
          <Typography variant="body2">{filterErrorDescription(errorDescription)}</Typography>
        ),
      });
    } else if (clientIsSSO) {
      configureForClient(client);
      if (isSignedOut(authState)) {
        federatedSignIn(ssoConfiguration.provider);
      }
    }
  }, [ssoConfiguration, authState]);

  if (isSignedOut(authState) && clientIsSSO) {
    return null;
  }

  function getProps() {
    return {
      authData: {
        ...data,
        password,
        username: storage.userEmail,
      },
      authState: data.state,
      onStateChange(state: AuthState, user: CognitoUser) {
        update({ state, user });
      },
      updateUsername(newUserName: string) {
        storage.userEmail = newUserName;
      },
      updatePassword(newPassword: string) {
        setPassword(newPassword);
      },
    } as IAuthContainerProps;
  }

  function renderComponent() {
    let AuthComponent;

    switch (data.state) {
      case AuthState.signIn:
        AuthComponent = SignIn;
        break;
      case AuthState.signUp:
        AuthComponent = SignUp;
        break;
      case AuthState.forgotPassword:
        AuthComponent = ForgotPassword;
        break;
      case AuthState.requireNewPassword:
        AuthComponent = RequireNewPassword;
        break;
      case AuthState.confirmSignUp:
        AuthComponent = ConfirmSignUp;
        break;
      case AuthState.invitation:
        AuthComponent = Invitation;
        break;
      case AuthState.clientSelect:
        AuthComponent = ClientSelect;
        break;
      case AuthState.suspended:
        AuthComponent = Suspended;
        break;
      default:
        throw new Error('Unhandled AuthState in auth/containers/Layout.tsx');
    }

    return <AuthComponent {...getProps()} />;
  }

  return (
    <Fragment>
      {renderComponent()}
      <RouteController {...getProps()} />
    </Fragment>
  );
};

export default withRouter(Layout);
